import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState, useMemo } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Header, VisitorBanner } from '@/components/layout'
import { useGroups } from '@/hooks/useGroups'
import { useDocumentHead } from '@/hooks/useDocumentHead'
import { groupService } from '@/services/groupService'
import { seasonService } from '@/services/seasonService'
import type { Group } from '@/services/groupService'
import type { SeasonWithBrackets, SignupSeasonWithState } from '@/services/seasonService'
import { RegisterInterestDialog } from '@/components/league'
import { registrationService } from '@/services/registrationService'
import { ArrowLeft, Lock } from 'lucide-react'

// Import refactored components
import {
  GroupHeader,
  UpcomingSignupsSection,
  ActiveSeasonsSection,
  LeaveGroupDialog,
  type Organiser,
} from '@/components/group-detail'

export function GroupDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth0()
  const { groups, isLoading: groupsLoading, refetch: refetchGroups } = useGroups()

  // State for public group data (when user is not a member or not authenticated)
  const [publicGroup, setPublicGroup] = useState<Group | null>(null)
  const [publicGroupLoading, setPublicGroupLoading] = useState(false)
  const [publicGroupError, setPublicGroupError] = useState<string | null>(null)
  
  // Summary state (seasons, leagues, organisers, member count - all in one call)
  const [activeSeasons, setActiveSeasons] = useState<SeasonWithBrackets[]>([])
  const [upcomingSignups, setUpcomingSignups] = useState<SignupSeasonWithState[]>([])
  const [leagueCount, setLeagueCount] = useState(0)
  const [memberCount, setMemberCount] = useState(0)
  const [organisers, setOrganisers] = useState<Organiser[]>([])
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [summaryError, setSummaryError] = useState<string | null>(null)
  
  // Leave group state
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false)
  const [leaveLoading, setLeaveLoading] = useState(false)
  const [leaveError, setLeaveError] = useState<string | null>(null)

  // Registration state
  const [registrationDialogSeason, setRegistrationDialogSeason] = useState<SignupSeasonWithState | null>(null)
  const [registrationSubmitting, setRegistrationSubmitting] = useState(false)
  const [registrationError, setRegistrationError] = useState<string | null>(null)
  
  // Try to find group from authenticated membership list - memoize to prevent re-renders
  const memberGroup = useMemo(() => groups.find(g => g.id === id), [groups, id])
  
  // Use member group if available, otherwise use public group data
  const group = memberGroup || publicGroup
  const isLoading = authLoading || (isAuthenticated && groupsLoading) || publicGroupLoading
  
  // Determine if we're viewing as a public/non-member user
  const isPublicView = !memberGroup && !!publicGroup
  
  // Track if user is a member (stable boolean for dependencies)
  const isMember = !!memberGroup

  // Leave group handler
  const handleLeaveGroup = async () => {
    if (!id) return
    setLeaveLoading(true)
    setLeaveError(null)
    try {
      await groupService.leave(id)
      setLeaveDialogOpen(false)
      refetchGroups()
      navigate('/groups')
    } catch (err) {
      console.error('Failed to leave group:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to leave group'
      setLeaveError(errorMessage)
    } finally {
      setLeaveLoading(false)
    }
  }

  // Registration handlers
  const handleOpenRegistration = (season: SignupSeasonWithState) => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } })
      return
    }
    setRegistrationError(null)
    setRegistrationDialogSeason(season)
  }

  const handleSubmitRegistration = async (bracketId: string) => {
    if (!registrationDialogSeason || !id) return

    setRegistrationSubmitting(true)
    setRegistrationError(null)

    try {
      await registrationService.register(
        registrationDialogSeason.leagueId,
        registrationDialogSeason.seasonId,
        bracketId
      )
      setRegistrationDialogSeason(null)
      // Refetch the summary to get updated registration state
      const summary = isMember
        ? await seasonService.getGroupSummary(id)
        : await seasonService.getGroupSummaryPublic(id)
      setUpcomingSignups(summary.upcomingSignups)
    } catch (err) {
      setRegistrationError((err as Error)?.message || 'Failed to register')
    } finally {
      setRegistrationSubmitting(false)
    }
  }

  const handleCancelRegistration = async (season: SignupSeasonWithState) => {
    if (season.myRegistrationStatus !== 'pending' || !id) return

    const confirmed = window.confirm('Are you sure you want to cancel your registration?')
    if (!confirmed) return

    try {
      // Need to get the registration ID - for now we'll query it
      const reg = await registrationService.getMyRegistration(season.leagueId, season.seasonId)
      if (reg) {
        await registrationService.cancel(season.leagueId, season.seasonId, reg.registrationId)
        // Refetch the summary to get updated registration state
        const summary = isMember
          ? await seasonService.getGroupSummary(id)
          : await seasonService.getGroupSummaryPublic(id)
        setUpcomingSignups(summary.upcomingSignups)
      }
    } catch (err) {
      console.error('Failed to cancel registration:', err)
    }
  }

  // Set page title
  useDocumentHead({
    title: group?.name || 'Group',
    description: group ? `${group.name} - ${group.memberCount} members on Badminton Leagues` : undefined,
    image: group?.icon?.startsWith('https://') ? group.icon : undefined,
  })

  // Fetch public group data if user is not a member or not authenticated
  useEffect(() => {
    // Skip if auth is still loading
    if (authLoading) return
    // Skip if we're loading groups and user is authenticated
    if (isAuthenticated && groupsLoading) return
    // Skip if user is already a member
    if (isMember) return
    // Skip if we already have public group data for this ID
    if (publicGroup?.id === id) return
    if (!id) return
    
    let cancelled = false
    
    async function fetchPublicGroup() {
      setPublicGroupLoading(true)
      setPublicGroupError(null)
      
      try {
        const data = await groupService.getPublic(id!)
        if (!cancelled) {
          setPublicGroup(data)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load public group:', err)
          setPublicGroupError(err instanceof Error ? err.message : 'Group not found or not public')
        }
      } finally {
        if (!cancelled) {
          setPublicGroupLoading(false)
        }
      }
    }
    
    fetchPublicGroup()
    
    return () => { cancelled = true }
  }, [id, authLoading, isAuthenticated, groupsLoading, isMember, publicGroup?.id])

  // Fetch group summary (seasons, organisers, league count, member count - all in one call)
  useEffect(() => {
    if (!id || !group) {
      setSummaryLoading(false)
      return
    }

    let cancelled = false

    async function fetchGroupSummary() {
      setSummaryLoading(true)
      setSummaryError(null)

      try {
        const summary = isMember
          ? await seasonService.getGroupSummary(id!)
          : await seasonService.getGroupSummaryPublic(id!)

        if (!cancelled) {
          setActiveSeasons(summary.activeSeasons)
          setUpcomingSignups(summary.upcomingSignups)
          setLeagueCount(summary.leagueCount)
          setMemberCount(summary.memberCount)
          setOrganisers(summary.organisers)
        }
      } catch (err) {
        console.error('Failed to load group summary:', err)
        if (!cancelled) {
          setSummaryError(err instanceof Error ? err.message : 'Failed to load data')
        }
      } finally {
        if (!cancelled) {
          setSummaryLoading(false)
        }
      }
    }

    fetchGroupSummary()

    return () => { cancelled = true }
  }, [id, group?.id, isMember])


  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          {/* Back button skeleton */}
          <div className="mb-6">
            <Skeleton className="h-8 w-32 mb-4" />

            {/* Header skeleton */}
            <div className="flex items-start gap-4">
              <Skeleton className="h-12 w-12 sm:h-16 sm:w-16 rounded-xl shrink-0" />
              <div className="space-y-2">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="h-5 w-64" />
                {/* Organisers skeleton */}
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-20" />
                  <div className="flex -space-x-1.5">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-6 w-6 rounded-full" />
                    ))}
                  </div>
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            </div>
          </div>

          {/* Active Seasons skeleton */}
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-40" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {[1, 2].map((i) => (
                <Card key={i} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-5 w-5 rounded" />
                        <Skeleton className="h-5 w-32" />
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </div>
                      <Skeleton className="h-4 w-24" />
                    </div>
                    <Skeleton className="h-5 w-5" />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </main>
      </div>
    )
  }

  if (!group && !isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <Lock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Group not accessible</h2>
            <p className="text-muted-foreground mb-4">
              {publicGroupError === 'This group is not public' 
                ? "This group is private. You need to be a member to view it."
                : "This group doesn't exist or is not publicly accessible."
              }
            </p>
            {!isAuthenticated ? (
              <div className="flex flex-col items-center gap-3">
                <p className="text-sm text-muted-foreground">
                  If you're a member, please sign in to access this group.
                </p>
                <Button onClick={() => loginWithRedirect({ appState: { returnTo: `/group/${id}` } })}>
                  Sign In
                </Button>
              </div>
            ) : (
              <Button onClick={() => navigate('/groups')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to My Groups
              </Button>
            )}
          </Card>
        </main>
      </div>
    )
  }

  // Should not happen after above checks, but satisfies TypeScript
  if (!group) return null

  
  // Only show admin actions if user is a member and admin
  const canManageGroup = memberGroup?.isAdmin
  const isOwner = memberGroup?.myRole === 'owner'

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {isPublicView && <VisitorBanner type="group" groupId={id} isPublicGroup={group?.isPublic} allowJoinRequests={group?.allowJoinRequests} />}
      
      <main className="container mx-auto px-4 py-8">
        <GroupHeader
          group={group}
          activeSeasons={activeSeasons}
          upcomingSignups={upcomingSignups}
          organisers={organisers}
          memberCount={memberCount}
          isAuthenticated={isAuthenticated}
          isMember={isMember}
          canManageGroup={!!canManageGroup}
          isOwner={!!isOwner}
          onLeaveGroup={() => {
            setLeaveError(null)
            setLeaveDialogOpen(true)
          }}
        />

        <UpcomingSignupsSection
          upcomingSignups={upcomingSignups}
          loading={summaryLoading}
          onOpenRegistration={handleOpenRegistration}
          onCancelRegistration={handleCancelRegistration}
        />

        <ActiveSeasonsSection
          groupId={id!}
          activeSeasons={activeSeasons}
          leagueCount={leagueCount}
          loading={summaryLoading}
          error={summaryError}
          canManageGroup={!!canManageGroup}
        />
      </main>

      {/* Leave Group Dialog */}
      <LeaveGroupDialog
        open={leaveDialogOpen}
        onOpenChange={(open) => {
          setLeaveDialogOpen(open)
          if (!open) setLeaveError(null)
        }}
        groupName={group?.name || ''}
        onLeave={handleLeaveGroup}
        loading={leaveLoading}
        error={leaveError}
      />

      {/* Registration Dialog */}
      {registrationDialogSeason && (
        <RegisterInterestDialog
          open={!!registrationDialogSeason}
          onOpenChange={(open) => { if (!open) setRegistrationDialogSeason(null) }}
          seasonName={registrationDialogSeason.name}
          seasonStartDate={registrationDialogSeason.startDate}
          seasonEndDate={registrationDialogSeason.endDate}
          leagueName={registrationDialogSeason.league.name}
          joiningFee={registrationDialogSeason.joiningFee}
          termsAndConditions={registrationDialogSeason.termsAndConditions}
          bankDetails={group?.bankDetails}
          showBankDetails={registrationDialogSeason.showBankDetails}
          paymentReference={registrationDialogSeason.paymentReference}
          brackets={registrationDialogSeason.brackets.map(b => ({ bracketId: b.bracketId, name: b.name }))}
          onSubmit={handleSubmitRegistration}
          submitting={registrationSubmitting}
          error={registrationError}
        />
      )}
    </div>
  )
}
